import { IBankAccount } from '@commenda/api';
import { Dialog, DialogContent } from '@commenda/kakashi';
import { formatAccountType } from '../bankAccount/utils';
import { classNames } from '@commenda/utils';
import { Dispatch, SetStateAction } from 'react';

const SummaryFieldKS = ({
  heading,
  value,
  hideBorder = false,
}: {
  heading: string;
  value: string | number | (string | number)[];
  hideBorder?: boolean;
}) => {
  return (
    <div
      className={classNames(
        'flex justify-between items-start py-1 gap-4',
        hideBorder ? '' : 'border-b border-neutral-6'
      )}
    >
      <span className="text-sm text-text-secondary flex-shrink-0 w-1/2">
        {heading}
      </span>
      <span className="text-sm font-medium text-neutral-1 text-left flex-grow w-2/3">
        {Array.isArray(value) ? (
          <div className="flex flex-col">
            {value.map((each, idx) => (
              <p key={idx} className="break-words">
                {each}
              </p>
            ))}
          </div>
        ) : (
          <p className="break-words">{value}</p>
        )}
      </span>
    </div>
  );
};

type BankAccountDetailsProp = {
  isShown: boolean;
  setIsShown: Dispatch<SetStateAction<boolean>>;
  bankAccountDetails: IBankAccount;
  title?: string;
  preventCloseOnOutsideClick?: boolean;
};

export const BankAccountDetailsModal = ({
  title = 'Bank Account Details',
  isShown,
  setIsShown,
  bankAccountDetails,
  preventCloseOnOutsideClick = false,
}: BankAccountDetailsProp) => {
  return (
    <Dialog open={isShown} onOpenChange={setIsShown}>
      <DialogContent
        className="p-6 flex flex-col gap-4"
        preventCloseOnOutsideClick={preventCloseOnOutsideClick}
      >
        <div>
          <h3 className="text-lg text-text-primary-900 mb-2">{title}</h3>
        </div>
        {bankAccountDetails ? (
          <div className="flex flex-col gap-2">
            <SummaryFieldKS
              heading="Name"
              value={bankAccountDetails?.bankName}
            />
            <SummaryFieldKS
              heading="Account Type"
              value={formatAccountType(bankAccountDetails?.subType)}
            />
            {bankAccountDetails?.credentials?.accountNumber ? (
              <SummaryFieldKS
                heading="Account Number"
                value={bankAccountDetails.credentials.accountNumber}
              />
            ) : null}
            {bankAccountDetails?.credentials?.ifscCode ? (
              <SummaryFieldKS
                heading="IFSC Code"
                value={bankAccountDetails.credentials.ifscCode}
              />
            ) : null}
            {bankAccountDetails?.credentials?.transitNumber ? (
              <SummaryFieldKS
                heading="Transit Number"
                value={bankAccountDetails.credentials.transitNumber}
              />
            ) : null}
            {bankAccountDetails?.credentials?.sortCode ? (
              <SummaryFieldKS
                heading="Sort Code"
                value={bankAccountDetails.credentials.sortCode}
              />
            ) : null}
            {bankAccountDetails?.credentials?.iban ? (
              <SummaryFieldKS
                heading="IBAN"
                value={bankAccountDetails.credentials.iban}
              />
            ) : null}
            {bankAccountDetails?.credentials?.routingNumber ? (
              <SummaryFieldKS
                heading="Routing Number"
                value={bankAccountDetails.credentials.routingNumber}
              />
            ) : null}
            {bankAccountDetails?.swiftCode ? (
              <SummaryFieldKS
                heading="Swift Code"
                value={bankAccountDetails?.swiftCode}
              />
            ) : null}
            {bankAccountDetails?.address ? (
              <SummaryFieldKS
                heading="Address Line 1"
                value={bankAccountDetails?.address?.addressLine1}
              />
            ) : null}
            {bankAccountDetails?.address?.addressLine2 ? (
              <SummaryFieldKS
                heading="Address Line 2"
                value={bankAccountDetails?.address?.addressLine2}
              />
            ) : null}
            {bankAccountDetails?.address?.city ? (
              <SummaryFieldKS
                heading="City"
                value={bankAccountDetails?.address?.city}
              />
            ) : null}
            {bankAccountDetails?.address?.state ? (
              <SummaryFieldKS
                heading="State"
                value={bankAccountDetails?.address?.state}
              />
            ) : null}
            {bankAccountDetails?.address?.postalCode ? (
              <SummaryFieldKS
                heading="Postal Code"
                value={bankAccountDetails?.address?.postalCode}
              />
            ) : null}
          </div>
        ) : (
          <div className="w-full h-full">Bank account not found.</div>
        )}
      </DialogContent>
    </Dialog>
  );
};
